import React from 'react'
import clsx from 'clsx'
import NextImage from 'next/image'
import NextLink from 'next/link'
import debounce from 'debounce'

import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@wartek-id/collapsible'
import { Icon } from '@wartek-id/icon'
import { InputGroup, InputRightAddon, Input } from '@wartek-id/input'
import { Text } from '@wartek-id/text'

import { isNavigator, isWebview } from 'utils/browser'
import {
  PUSAT_INFORMASI_SEARCH_BASE_URL,
  KURIKULUM_MERDEKA_CONTENTS,
  FACT_AND_MISCONCEPTION_CONTENTS,
} from '../constants'
import { useSETrackEvent } from 'app/KurikulumMerdekaPage/hooks/useSETrackEvent'

interface ILinkProps {
  isExternalLink?: boolean
  href: string
  children: React.ReactNode
  target?: string
  onClick: () => void
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string
  href: string
  imageSrc: string
}
const Card = ({ text, href, imageSrc, ...props }: CardProps) => {
  return (
    <div className="col-span-12" {...props}>
      <a href={href}>
        <div className="p-3 rounded-md bg-[#4C8DF6] flex flex-row justify-between">
          <div className="flex grow-0">
            <NextImage
              layout="fixed"
              src={imageSrc}
              alt={text}
              width={24}
              height={24}
            />
          </div>
          <Text
            color="inverse"
            variant="body-sm"
            className="grow mx-3 !font-semibold"
          >
            {text}
          </Text>
          <Icon color="inverse">chevron_right</Icon>
        </div>
      </a>
    </div>
  )
}

type TTentangKurikulumMerdekaProps = {
  appDeviceID: string
}
const TentangKurikulumMerdeka = ({
  appDeviceID,
}: TTentangKurikulumMerdekaProps) => {
  const isOpenedOnWebview = isNavigator ? isWebview() : true
  const [activeKey, setActiveKey] = React.useState<string | undefined>()
  const [keyword, setKeyword] = React.useState<string | undefined>()
  const {
    SETrackPageLoaded,
    SETrackSearchSubmitted,
    SETrackAccordionClicked,
    SETrackContentClicked,
  } = useSETrackEvent({
    appDeviceID: appDeviceID,
  })

  const toggleHandler = React.useCallback(
    (open, index, key) => {
      const isKurikulumMerdekaContent = key.includes('kurikulum-merdeka')
      SETrackAccordionClicked({
        activityType: `CLICK_TENTANG_KURIKULUM_MERDEKA_${
          isKurikulumMerdekaContent ? 'ACCORDION' : 'MISCONCEPTION'
        }`,
        index: index + 1,
        pageElement: (isKurikulumMerdekaContent
          ? KURIKULUM_MERDEKA_CONTENTS
          : FACT_AND_MISCONCEPTION_CONTENTS)?.[index]?.title,
        action: activeKey === key ? 'collapse' : 'expand',
      })

      if (open) {
        setActiveKey(key)
      } else if (activeKey === key) {
        setActiveKey(undefined)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeKey]
  )

  const searchHandler = () => {
    if (!keyword) {
      return
    }

    SETrackSearchSubmitted({
      submittedInput: keyword,
    })

    window.location.href = `${PUSAT_INFORMASI_SEARCH_BASE_URL}${keyword.replace(
      /\s/g,
      '+'
    )}`
  }

  const Link = ({
    isExternalLink = false,
    href,
    children,
    target,
    onClick,
  }: ILinkProps) => {
    return isExternalLink ? (
      <a href={href} target={target} onClick={onClick}>
        {children}
      </a>
    ) : (
      <NextLink href={href} passHref target={target}>
        <a onClick={onClick}>{children}</a>
      </NextLink>
    )
  }

  React.useEffect(() => {
    SETrackPageLoaded()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={clsx('leading-loose pt-14', isOpenedOnWebview && '!pt-4')}>
      <div className="px-4">
        <div className="mb-6">
          <InputGroup data-testid="kurikulum-merdeka-search">
            <Input
              data-testid="kurikulum-merdeka-search-input"
              clearable
              placeholder="Cari informasi Kurikulum Merdeka"
              onChange={debounce((e) => {
                setKeyword(e.target.value)
              }, 100)}
              onKeyDown={(e) => {
                if (e.key == 'Enter') {
                  searchHandler()
                }
              }}
            />
            <InputRightAddon
              data-testid="kurikulum-merdeka-search-trigger"
              style={{ pointerEvents: 'auto' }}
              onClick={searchHandler}
            >
              <Icon>search</Icon>
            </InputRightAddon>
          </InputGroup>
        </div>

        {KURIKULUM_MERDEKA_CONTENTS.map((item, index) => {
          const keyPrefix = 'kurikulum-merdeka'
          const isOpen = activeKey === `${keyPrefix}-item-${index}`

          return (
            <Collapsible
              key={`${keyPrefix}-item-${index}`}
              data-testid={`${keyPrefix}-item`}
              className="bg-white"
              open={isOpen}
              onToggle={(open) =>
                toggleHandler(open, index, `${keyPrefix}-item-${index}`)
              }
            >
              <CollapsibleTrigger className="text-left !px-0 transition-all ease-in-out !border-b-0">
                <div className="space-y-1">
                  <Text variant="heading-sm">{item.title}</Text>
                  <Text
                    variant="helper-bold"
                    color="informational"
                    className="!text-blue-60"
                  >
                    {item.subtitle}
                  </Text>
                </div>
                <Icon color="informational">
                  {isOpen ? 'expand_less' : 'expand_more'}
                </Icon>
              </CollapsibleTrigger>
              <CollapsibleContent className="pb-4 transition-all ease-in-out">
                <div
                  data-testid={`${keyPrefix}-content-${index}`}
                  className="space-y-2 break-words"
                >
                  {(item.children ?? []).map((child, childIndex) => {
                    return (
                      <Card
                        key={`${keyPrefix}-item-${index}-${childIndex}`}
                        text={child.title}
                        href={child.href}
                        imageSrc={child.icon}
                        onClick={() => {
                          SETrackContentClicked({
                            articleUrl: child.href,
                          })
                        }}
                      />
                    )
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )
        })}

        <Link
          href={process.env.NEXT_PUBLIC_ABOUT_KM_URL}
          onClick={() =>
            SETrackContentClicked({
              articleUrl: `${process.env.NEXT_PUBLIC_BASE_URL}${process.env.NEXT_PUBLIC_ABOUT_KM_URL}`,
            })
          }
        >
          <div className="flex items-start justify-between p-4 my-6 border rounded-lg cursor-pointer border-subdued">
            <NextImage
              src="/icon/document-collection.svg"
              alt="Kumpulan Dokumen"
              width={32}
              height={32}
            />
            <div>
              <Text
                variant="action"
                className="flex-grow mx-3 !font-semibold mb-1"
              >
                Kumpulan dokumen rujukan panduan pelaksanaan Kurikulum Merdeka
              </Text>
              <Text variant="body-sm" className="flex-grow mx-3 !text-subdued">
                Panduan kurikulum, Capaian pembelajaran, Elemen CP dan ATP.
              </Text>
            </div>
            <div className="flex flex-col justify-center">
              <Icon color="default">chevron_right</Icon>
            </div>
          </div>
        </Link>
      </div>

      <div className="bg-[#ECF5FE] px-4 py-6 space-y-2">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-grow-0 mr-2">
            <NextImage
              layout="fixed"
              src="/icon/km-speaker.png"
              alt="Fakta Implementasi Kurikulum Merdeka"
              width={40}
              height={40}
            />
          </div>
          <Text variant="heading-sm" className="flex-grow mx-3 !font-semibold">
            <span className="text-blue-60">Ungkap Fakta</span> Mengenai
            Implementasi Kurikulum Merdeka
          </Text>
        </div>

        {FACT_AND_MISCONCEPTION_CONTENTS.map((item, index) => {
          const keyPrefix = 'fact-and-misconception'
          const isOpen = activeKey === `${keyPrefix}-item-${index}`

          return (
            <Collapsible
              key={`${keyPrefix}-item-${index}`}
              data-testid={`${keyPrefix}-item`}
              open={isOpen}
              className="bg-white border rounded-lg border-subdued"
              onToggle={(open) =>
                toggleHandler(open, index, `${keyPrefix}-item-${index}`)
              }
            >
              <CollapsibleTrigger className="text-left">
                <Text variant="heading-sm">{item.title}</Text>
                <Icon>{isOpen ? 'expand_less' : 'expand_more'}</Icon>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div
                  data-testid={`${keyPrefix}-content-${index}`}
                  className="p-4 break-words"
                >
                  <Text variant="body">{item.content}</Text>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )
        })}
      </div>
    </div>
  )
}

export default TentangKurikulumMerdeka
